import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaPlay, FaPause, FaBookmark, FaBookOpen, FaHeadphones, FaTimes, FaCheck } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { allVocabulary } from '../data/vocabularyIndex';
import { getCorrectAudioUrl } from '../utils/audioUrlMapper';
import { getAudioUrl } from '../data/vocabularyAudioMap';

interface VocabularyWord {
  id: string;
  persian: string;
  english: string;
  transliteration?: string;
  example?: string;
  category?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  audioUrl?: string;
  partOfSpeech?: string;
  tags?: string[];
}

const VocabularyCategory: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();

  const categoryConfigs: Record<string, any> = {
    kitchen: {
      title: 'Kitchen Vocabulary',
      icon: '🍳',
      color: 'red',
      gradient: 'from-red-400 to-red-600',
      description: 'Learn essential kitchen and cooking vocabulary in Persian'
    },
    colors: {
      title: 'Colors',
      icon: '🎨',
      color: 'purple',
      gradient: 'from-purple-400 to-purple-600',
      description: 'Master color names and descriptions in Persian'
    },
    animals: {
      title: 'Animals',
      icon: '🐾',
      color: 'green',
      gradient: 'from-green-400 to-green-600',
      description: 'Discover animal names and sounds in Persian'
    },
    body: {
      title: 'Body Parts',
      icon: '👤',
      color: 'blue',
      gradient: 'from-blue-400 to-blue-600',
      description: 'Learn body parts and health-related vocabulary'
    },
    family: {
      title: 'Family',
      icon: '👨‍👩‍👧‍👦',
      color: 'pink',
      gradient: 'from-pink-400 to-pink-600',
      description: 'Family members and relationships in Persian'
    },
    food: {
      title: 'Food & Drinks',
      icon: '🍎',
      color: 'orange',
      gradient: 'from-orange-400 to-orange-600',
      description: 'Food, drinks, and dining vocabulary'
    },
    nature: {
      title: 'Nature',
      icon: '🌳',
      color: 'green',
      gradient: 'from-green-400 to-green-600',
      description: 'Natural world and environmental vocabulary'
    },
    home: {
      title: 'Home & Living',
      icon: '🏠',
      color: 'yellow',
      gradient: 'from-yellow-400 to-yellow-600',
      description: 'Household items and living spaces'
    },
    travel: {
      title: 'Travel & Transportation',
      icon: '✈️',
      color: 'cyan',
      gradient: 'from-cyan-400 to-cyan-600',
      description: 'Travel, transportation, and journey vocabulary'
    },
    weather: {
      title: 'Weather & Seasons',
      icon: '🌤️',
      color: 'blue',
      gradient: 'from-blue-400 to-blue-600',
      description: 'Weather conditions and seasonal vocabulary'
    },
    emotions: {
      title: 'Emotions & Feelings',
      icon: '😊',
      color: 'yellow',
      gradient: 'from-yellow-400 to-yellow-600',
      description: 'Express emotions and feelings in Persian'
    },
    numbers: {
      title: 'Numbers',
      icon: '🔢',
      color: 'indigo',
      gradient: 'from-indigo-400 to-indigo-600',
      description: 'Learn counting and numerical expressions'
    },
    time: {
      title: 'Time & Calendar',
      icon: '⏰',
      color: 'gray',
      gradient: 'from-gray-400 to-gray-600',
      description: 'Time expressions, days, months, and dates'
    },
    clothing: {
      title: 'Clothing & Fashion',
      icon: '👕',
      color: 'purple',
      gradient: 'from-purple-400 to-purple-600',
      description: 'Clothing items and fashion vocabulary'
    },
    sports: {
      title: 'Sports & Activities',
      icon: '⚽',
      color: 'green',
      gradient: 'from-green-400 to-green-600',
      description: 'Sports, games, and recreational activities'
    },
    technology: {
      title: 'Technology',
      icon: '💻',
      color: 'blue',
      gradient: 'from-blue-400 to-blue-600',
      description: 'Technology and digital device vocabulary'
    },
    professions: {
      title: 'Jobs & Professions',
      icon: '👨‍💼',
      color: 'gray',
      gradient: 'from-gray-400 to-gray-600',
      description: 'Career and profession vocabulary'
    },
    education: {
      title: 'Education & School',
      icon: '📚',
      color: 'blue',
      gradient: 'from-blue-400 to-blue-600',
      description: 'Educational and academic vocabulary'
    },
    city: {
      title: 'City & Urban Life',
      icon: '🏙️',
      color: 'indigo',
      gradient: 'from-indigo-400 to-indigo-600',
      description: 'City names and urban vocabulary in Persian'
    },
    business: {
      title: 'Business & Professional',
      icon: '💼',
      color: 'blue',
      gradient: 'from-blue-400 to-blue-600',
      description: 'Essential business and professional vocabulary in Persian'
    }
  };

  const config = categoryConfigs[category || ''] || {
    title: 'Vocabulary',
    icon: '📚',
    color: 'lavender',
    gradient: 'from-lavender-400 to-lavender-600',
    description: 'Learn Persian vocabulary'
  };

  useEffect(() => {
    loadFavorites();
  }, []);

  useEffect(() => {
    const loadWords = async () => {
    try {
      setLoading(true);
        setError(null);

        if (!category) {
          setError('No category specified');
          return;
        }

        // Category mappings for comprehensive vocabulary
        const categoryMappings: Record<string, string[]> = {
          kitchen: ['Kitchen', 'kitchen', 'Food'],
          colors: ['Colors', 'colors'],
          animals: ['Animals', 'animals'],
          'body-parts': ['Body', 'body', 'Body Parts'],
          body: ['Body', 'body', 'Body Parts'],
          family: ['Family', 'family'],
          food: ['Food', 'food', 'drinks'],
          nature: ['Nature', 'nature', 'environment', 'Weather'],
          home: ['Home', 'home', 'household'],
          travel: ['Travel', 'travel', 'transportation'],
          weather: ['Weather', 'weather', 'seasons', 'Nature'],
          emotions: ['Emotions', 'emotions', 'feelings'],
          feelings: ['Emotions', 'emotions', 'feelings'],
          numbers: ['Numbers', 'numbers', 'counting'],
          time: ['Time', 'time', 'calendar'],
          clothing: ['Clothing', 'clothing', 'fashion'],
          sports: ['Sports', 'sports', 'activities'],
          technology: ['Technology', 'technology', 'digital'],
          professions: ['Professions', 'professions', 'jobs'],
          education: ['Education', 'education', 'school'],
          greetings: ['Greetings', 'greetings'],
          city: ['City', 'city'],
          business: ['Business', 'business']
        };

        const categoryLower = category.toLowerCase();
      const mappedCategories = categoryMappings[categoryLower] || [category?.charAt(0).toUpperCase() + category?.slice(1).toLowerCase()];
      
        const filteredWords = allVocabulary.filter(word => 
        mappedCategories.some(cat => 
          word.category?.toLowerCase() === cat.toLowerCase()
        )
      );
      

      
        if (filteredWords.length === 0) {
          // Fallback to sample words if no words found
          const sampleWords = getSampleWords(category);
          setWords(sampleWords);
        } else {
          setWords(filteredWords);
        }
      } catch (err) {
        console.error('Error loading vocabulary:', err);
        setError('Failed to load vocabulary');
    } finally {
      setLoading(false);
    }
  };

    loadWords();
  }, [category]);

  // Function to get sample words if no data is found
  const getSampleWords = (category: string): VocabularyWord[] => {
    return [];
  };

  // Properly implement loadFavorites
  const loadFavorites = () => {
    const savedFavorites = localStorage.getItem('vocabulary-favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  };

  const saveFavorites = (newFavorites: string[]) => {
    setFavorites(newFavorites);
    localStorage.setItem('vocabulary-favorites', JSON.stringify(newFavorites));
  };

  const toggleFavorite = (wordId: string) => {
    const newFavorites = favorites.includes(wordId)
      ? favorites.filter(id => id !== wordId)
      : [...favorites, wordId];
    
    saveFavorites(newFavorites);
  };

  const removeFavorite = (wordId: string) => {
    const newFavorites = favorites.filter(id => id !== wordId);
    saveFavorites(newFavorites);
  };

  const clearAllFavorites = () => {
    if (window.confirm('Are you sure you want to remove all favorites?')) {
      saveFavorites([]);
      setShowFavoritesModal(false);
    }
  };

  // Helper function to check if audio file exists
  const checkAudioExists = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  const playAudio = async (audioUrl: string, wordId: string) => {
    // First try to find the word in the original words array
    let word = words.find((w, index) => `${w.id || w.persian}-${index}` === wordId);
    
    // If not found in original array, try to find it by word content (for filtered scenarios)
    if (!word) {
      const [wordText] = wordId.split('-');
      word = words.find(w => w.persian === wordText || w.id === wordText);
    }
    
    if (!word) {
      console.log('Word not found for audio playback');
      return;
    }

    // Stop current audio if playing
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    if (currentlyPlaying === wordId) {
      setCurrentlyPlaying(null);
      setAudio(null);
      return;
    }

    // Set as playing immediately
    setCurrentlyPlaying(wordId);

    // Get the correct audio URL using the mapper, or try vocabulary audio map if no audioUrl
    let correctedAudioUrl = audioUrl;
    
    // If no audioUrl provided, try to get from vocabulary audio map
    if (!audioUrl || audioUrl === '') {
      correctedAudioUrl = getAudioUrl(word.english, word.category) || '';
      console.log(`🎵 Using vocabulary audio map for "${word.english}": ${correctedAudioUrl}`);
    } else {
      // Use the lesson audio mapper for existing audioUrls
      correctedAudioUrl = await getCorrectAudioUrl('', audioUrl, word.category);
      console.log(`🔄 Audio URL mapping: ${audioUrl} -> ${correctedAudioUrl}`);
    }

    // Check if audio URL exists and is valid
    if (correctedAudioUrl) {
      // For vocabulary audio URLs (static files), skip the existence check
      const isVocabularyAudio = correctedAudioUrl.startsWith('/vocabulary/') || correctedAudioUrl.startsWith('/audio/vocabulary/');
      let audioExists = isVocabularyAudio;
      
      // Only check existence for non-vocabulary audio URLs
      if (!isVocabularyAudio) {
        audioExists = await checkAudioExists(correctedAudioUrl);
      }
      
      if (audioExists) {
        try {
          // Create new audio instance
          const newAudio = new Audio(correctedAudioUrl);
          
          newAudio.onended = () => {
            setCurrentlyPlaying(null);
            setAudio(null);
          };
          
          newAudio.onerror = (e) => {
            console.error('Audio playback error:', e);
            setCurrentlyPlaying(null);
            setAudio(null);
          };
            
          setAudio(newAudio);
          await newAudio.play();
          return;
        } catch (error) {
          console.error('Error playing audio file:', error);
        }
      } else {
        console.log('Audio file not found for:', word.persian);
      }
    }

    // No fallback - audio files should be available
    console.log('No audio available for:', word.persian);
    setCurrentlyPlaying(null);
  };

  const saveAllToGarden = async () => {
    if (!user) {
      alert('Please log in to save words to your garden');
      return;
    }

    try {
      const wordsToSave = favorites.map(wordId => {
        const word = words.find((w, index) => `${w.id || w.persian}-${index}` === wordId);
        return word;
      }).filter(Boolean);

      if (wordsToSave.length === 0) {
        alert('No favorite words to save');
        return;
      }

      // Here you would typically send the words to your backend
      // For now, we'll just show a success message
      console.log('Saving words to garden:', wordsToSave);
      
      // You can implement the actual API call here
      // const response = await fetch('/api/vocabulary/save-to-garden', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${user.token}`
      //   },
      //   body: JSON.stringify({ words: wordsToSave })
      // });

      alert(`Successfully saved ${wordsToSave.length} words to your garden!`);
      setShowFavoritesModal(false);
    } catch (error) {
      console.error('Error saving to garden:', error);
      alert('Failed to save words to garden');
    }
  };

  // Filter words based on search term and favorites
  const filteredWords = words.filter(word => {
    const matchesSearch = searchTerm === '' || 
      word.persian.toLowerCase().includes(searchTerm.toLowerCase()) ||
      word.english.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (word.transliteration && word.transliteration.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (showFavoritesOnly) {
      const wordIndex = words.indexOf(word);
      const wordId = `${word.id || word.persian}-${wordIndex}`;
      return matchesSearch && favorites.includes(wordId);
    }
    
    return matchesSearch;
  });

    return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${config.gradient} text-white py-16`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className="text-center lg:text-left mb-8 lg:mb-0">
          <button 
                onClick={() => navigate(-1)}
                className="mb-4 text-white text-opacity-80 hover:text-opacity-100 transition-colors"
          >
                ← Back to Categories
          </button>
              
              {user && (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowFavoritesModal(true)}
                    className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
                  >
                    <FaBookmark className="text-lg" />
                    <span>Favorites ({favorites.length})</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <div className="flex justify-center items-center space-x-4 mb-4">
              <span className="text-4xl">{config.icon}</span>
              <h1 className="text-3xl md:text-4xl font-bold">{config.title}</h1>
            </div>
            <p className="text-lg text-white text-opacity-90 max-w-2xl mx-auto">
              {config.description}
            </p>
            <div className="mt-6 flex justify-center space-x-6 text-white text-opacity-80">
              <div className="flex items-center space-x-2">
                <FaBookOpen className="text-lg" />
                <span>{words.length} words</span>
              </div>
              <div className="flex items-center space-x-2">
                <FaHeadphones className="text-lg" />
                <span>Audio included</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
        </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
        ) : (
          <>
            {/* Search and Filter Controls */}
            <div className="mb-6">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex-1 max-w-md">
                <input
                  type="text"
                    placeholder="Search words..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
            </div>

                {user && (
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center space-x-2 text-gray-700">
                      <input
                        type="checkbox"
                        checked={showFavoritesOnly}
                        onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                        className="rounded"
                      />
                      <span>Show favorites only</span>
                    </label>
            </div>
                )}
        </div>
      </div>

            {/* Vocabulary Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredWords.map((word, index) => {
                // Ensure we have both persian and english text before rendering
                if (!word.persian || !word.english) {
                  return null;
                }
                
                const wordId = `${word.id || word.persian}-${index}`;
                const isFavorite = favorites.includes(wordId);
                const isPlaying = currentlyPlaying === wordId;
                
                return (
                  <div
                    key={wordId}
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-800 mb-2" dir="rtl">
                          {word.persian}
                        </h3>
                        <p className="text-lg text-gray-600 mb-1" dir="ltr">
                          {word.english}
                        </p>
                        {word.transliteration && (
                          <p className="text-sm text-gray-500 italic" dir="ltr">
                            {word.transliteration}
                          </p>
                        )}
                      </div>
                      
                      {user && (
                        <button
                          onClick={() => toggleFavorite(wordId)}
                          className={`p-2 rounded-full transition-colors ${
                            isFavorite
                              ? 'text-yellow-500 hover:text-yellow-600'
                              : 'text-gray-400 hover:text-yellow-500'
                          }`}
                        >
                          <FaBookmark className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    {word.example && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700" dir="rtl">
                          <span className="font-semibold">Example:</span> {word.example}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        word.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                        word.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {word.difficulty || 'beginner'}
                      </span>
                      
                      <button
                        onClick={() => playAudio(word.audioUrl || '', wordId)}
                        className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors bg-blue-500 hover:bg-blue-600 text-white"
                        title="Play audio"
                      >
                        {isPlaying ? (
                          <FaPause className="w-4 h-4" />
                        ) : (
                          <FaPlay className="w-4 h-4" />
                        )}
                        <span>Listen</span>
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>

            {filteredWords.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  {showFavoritesOnly ? 'No favorite words found.' : 'No words found matching your search.'}
                </p>
          </div>
            )}
          </>
        )}
      </div>

      {/* Favorites Modal */}
      {showFavoritesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Your Favorites</h3>
                <button
                  onClick={() => setShowFavoritesModal(false)}
                className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
            </div>

            {favorites.length === 0 ? (
              <p className="text-gray-500">No favorites yet.</p>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {favorites.map(wordId => {
                    const word = words.find((w, index) => `${w.id || w.persian}-${index}` === wordId);
                    if (!word) return null;
                    
                    return (
                      <div key={wordId} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-semibold" dir="rtl">{word.persian}</span>
                          <span className="text-gray-500 ml-2" dir="ltr">({word.english})</span>
                        </div>
                        <button
                          onClick={() => removeFavorite(wordId)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                    </div>
                
                <div className="flex space-x-2">
              <button
                    onClick={clearAllFavorites}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={saveAllToGarden}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                <FaCheck className="w-5 h-5" />
                <span>Save All to Garden</span>
              </button>
            </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabularyCategory; 